
/**
 * Gallery for lbbw-am.de
 * Plain JavaScript conversion from jQuery Slick Carousel
 * Now includes HeroSlider functionality
 *
 * @author Converted to Plain JS
 */
import AMApp from '../../global/app';

export class Gallery {
  constructor(element, options = {}) {
    this.element = element;

    const defaults = {
      dots: true,
      infinite: true,
      speed: 500,
      fade: true,
      cssEase: 'linear',
      autoplay: false,
      autoplaySpeed: 3000,
      pauseOnHover: true,
      pauseOnFocus: true,
      prevArrow: '<button class="gallery-btn gallery-btn__prev" aria-label="Vorheriges Bild"></button>',
      nextArrow: '<button class="gallery-btn gallery-btn__next" aria-label="Nächstes Bild"></button>',
      customPaging: null, // Function for custom dot content
      responsive: [
        {
          breakpoint: AMApp?.CONSTANTS?.BREAKPOINT?.MD || 768,
          settings: {
            arrows: false,
            dots: true,
          }
        },
        {
          breakpoint: AMApp?.CONSTANTS?.BREAKPOINT?.SM || 576,
          settings: {
            arrows: false,
            dots: true,
          }
        }
      ]
    };

    this.options = Object.assign(defaults, options);

    // State
    this.currentSlide = 0;
    this.slides = [];
    this.slidesContainer = null;
    this.dotsContainer = null;
    this.prevButton = null;
    this.nextButton = null;
    this.totalSlides = 0;
    this.isTransitioning = false;
    this.autoplayTimer = null;
    this.isPaused = false;

    this.init();
  }

  /**
   * Initialize gallery
   */
  init() {
    console.log('Gallery initialized');
    this.cacheSlides();
    this.createStructure();
    this.attachEventListeners();
    this.updateResponsiveSettings();
    this.goToSlide(0, false);

    // Start autoplay if enabled
    if (this.options.autoplay) {
      this.startAutoplay();
    }

    // Handle resize
    window.addEventListener('resize', () => {
      this.updateResponsiveSettings();
    });
  }

  /**
   * Cache slide elements
   */
  cacheSlides() {
    this.slides = Array.from(this.element.children);
    this.totalSlides = this.slides.length;

    if (this.totalSlides === 0) return;

    // Add slide classes and hide all initially
    this.slides.forEach((slide, index) => {
      slide.classList.add('gallery-slide');
      slide.setAttribute('data-slide', index);
      slide.style.display = 'none';
      slide.setAttribute('aria-hidden', 'true');
    });
  }

  /**
   * Create gallery structure
   */
  createStructure() {
    if (this.totalSlides === 0) return;

    // Create main container
    this.element.classList.add('gallery-container');

    // Create slides container
    this.slidesContainer = document.createElement('div');
    this.slidesContainer.className = 'gallery-slides';

    // Move slides to container
    this.slides.forEach(slide => {
      this.slidesContainer.appendChild(slide);
    });

    this.element.appendChild(this.slidesContainer);

    // Create navigation arrows
    this.createArrows();

    // Create dots
    this.createDots();
  }

  /**
   * Create navigation arrows
   */
  createArrows() {
    if (!this.shouldShowArrows()) return;

    const arrowsContainer = document.createElement('div');
    arrowsContainer.className = 'gallery-arrows';

    // Previous button
    const prevButtonContainer = document.createElement('div');
    prevButtonContainer.innerHTML = this.options.prevArrow;
    this.prevButton = prevButtonContainer.firstElementChild;
    this.prevButton.addEventListener('click', () => this.prevSlide());

    // Next button
    const nextButtonContainer = document.createElement('div');
    nextButtonContainer.innerHTML = this.options.nextArrow;
    this.nextButton = nextButtonContainer.firstElementChild;
    this.nextButton.addEventListener('click', () => this.nextSlide());

    arrowsContainer.appendChild(this.prevButton);
    arrowsContainer.appendChild(this.nextButton);
    this.element.appendChild(arrowsContainer);
  }

  /**
   * Create dots navigation
   */
  createDots() {
    if (!this.options.dots) return;

    this.dotsContainer = document.createElement('div');
    this.dotsContainer.className = 'gallery-dots';
    this.dotsContainer.setAttribute('role', 'tablist');

    for (let i = 0; i < this.totalSlides; i++) {
      const dot = document.createElement('button');
      dot.className = 'gallery-dot';
      dot.setAttribute('data-slide', i);
      dot.setAttribute('role', 'tab');

      // Custom paging for hero slider
      if (this.options.customPaging && typeof this.options.customPaging === 'function') {
        const customContent = this.options.customPaging(this, i);
        if (typeof customContent === 'string') {
          dot.innerHTML = customContent;
        } else if (customContent instanceof Element) {
          dot.appendChild(customContent);
        }
      } else {
        dot.setAttribute('aria-label', `Gehe zu Bild ${i + 1}`);
        dot.textContent = i + 1;
      }

      dot.addEventListener('click', () => this.goToSlide(i));
      this.dotsContainer.appendChild(dot);
    }

    this.element.appendChild(this.dotsContainer);
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Keyboard navigation
    this.element.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prevSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.totalSlides - 1);
          break;
      }
    });

    // Pause on hover
    if (this.options.pauseOnHover && this.options.autoplay) {
      this.element.addEventListener('mouseenter', () => {
        this.pauseAutoplay();
      });

      this.element.addEventListener('mouseleave', () => {
        this.resumeAutoplay();
      });
    }

    // Pause on focus
    if (this.options.pauseOnFocus && this.options.autoplay) {
      this.element.addEventListener('focusin', () => {
        this.pauseAutoplay();
      });

      this.element.addEventListener('focusout', () => {
        this.resumeAutoplay();
      });
    }

    // Touch/swipe support (basic)
    let startX = 0;
    let endX = 0;

    this.element.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.element.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      this.handleSwipe();
    }, { passive: true });

    // Mouse drag support
    let isDragging = false;
    let startMouseX = 0;

    this.element.addEventListener('mousedown', (e) => {
      isDragging = true;
      startMouseX = e.clientX;
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
    });

    document.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      isDragging = false;
      endX = e.clientX;
      startX = startMouseX;
      this.handleSwipe();
    });

    const handleSwipe = () => {
      const threshold = 50; // Minimum distance for swipe
      const distance = startX - endX;

      if (Math.abs(distance) > threshold) {
        if (distance > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }
    };

    this.handleSwipe = handleSwipe;
  }

  /**
   * Start autoplay
   */
  startAutoplay() {
    if (!this.options.autoplay || this.totalSlides <= 1) return;

    this.autoplayTimer = setInterval(() => {
      if (!this.isPaused) {
        this.nextSlide();
      }
    }, this.options.autoplaySpeed);
  }

  /**
   * Stop autoplay
   */
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  /**
   * Pause autoplay
   */
  pauseAutoplay() {
    this.isPaused = true;
  }

  /**
   * Resume autoplay
   */
  resumeAutoplay() {
    this.isPaused = false;
  }

  /**
   * Go to specific slide
   */
  goToSlide(index, animate = true) {
    if (this.isTransitioning || index === this.currentSlide) return;

    if (index < 0) {
      index = this.options.infinite ? this.totalSlides - 1 : 0;
    } else if (index >= this.totalSlides) {
      index = this.options.infinite ? 0 : this.totalSlides - 1;
    }

    const previousSlide = this.currentSlide;
    this.currentSlide = index;

    if (animate) {
      this.animateSlide(previousSlide, index);
    } else {
      this.showSlide(index);
    }

    this.updateNavigation();
  }

  /**
   * Show slide without animation
   */
  showSlide(index) {
    this.slides.forEach((slide, i) => {
      if (i === index) {
        slide.style.display = 'block';
        slide.setAttribute('aria-hidden', 'false');
        slide.classList.add('gallery-slide-active');
      } else {
        slide.style.display = 'none';
        slide.setAttribute('aria-hidden', 'true');
        slide.classList.remove('gallery-slide-active');
      }
    });
  }

  /**
   * Animate slide transition
   */
  animateSlide(from, to) {
    if (!this.options.fade) {
      this.showSlide(to);
      return;
    }

    this.isTransitioning = true;

    const fromSlide = this.slides[from];
    const toSlide = this.slides[to];

    // Show both slides for fade transition
    toSlide.style.display = 'block';
    toSlide.style.opacity = '0';
    toSlide.setAttribute('aria-hidden', 'false');
    toSlide.classList.add('gallery-slide-active');

    fromSlide.classList.remove('gallery-slide-active');

    // Force reflow
    toSlide.offsetHeight;

    // Add transition
    const duration = this.options.speed;
    toSlide.style.transition = `opacity ${duration}ms ${this.options.cssEase}`;
    fromSlide.style.transition = `opacity ${duration}ms ${this.options.cssEase}`;

    // Start fade
    toSlide.style.opacity = '1';
    fromSlide.style.opacity = '0';

    // Complete transition
    setTimeout(() => {
      fromSlide.style.display = 'none';
      fromSlide.setAttribute('aria-hidden', 'true');

      // Reset styles
      toSlide.style.transition = '';
      fromSlide.style.transition = '';
      fromSlide.style.opacity = '';
      toSlide.style.opacity = '';

      this.isTransitioning = false;
    }, duration);
  }

  /**
   * Go to next slide
   */
  nextSlide() {
    this.goToSlide(this.currentSlide + 1);
  }

  /**
   * Go to previous slide
   */
  prevSlide() {
    this.goToSlide(this.currentSlide - 1);
  }

  /**
   * Update navigation states
   */
  updateNavigation() {
    // Update dots
    if (this.dotsContainer) {
      const dots = this.dotsContainer.querySelectorAll('.gallery-dot');
      dots.forEach((dot, index) => {
        if (index === this.currentSlide) {
          dot.classList.add('gallery-dot-active');
          dot.setAttribute('aria-selected', 'true');
        } else {
          dot.classList.remove('gallery-dot-active');
          dot.setAttribute('aria-selected', 'false');
        }
      });
    }

    // Update arrows
    if (this.prevButton && this.nextButton && !this.options.infinite) {
      this.prevButton.disabled = this.currentSlide === 0;
      this.nextButton.disabled = this.currentSlide === this.totalSlides - 1;
    }
  }

  /**
   * Check if arrows should be shown based on responsive settings
   */
  shouldShowArrows() {
    const currentSettings = this.getCurrentSettings();
    return currentSettings.arrows !== false;
  }

  /**
   * Get current settings based on responsive breakpoints
   */
  getCurrentSettings() {
    const windowWidth = window.innerWidth;
    let currentSettings = { ...this.options };

    // Apply responsive settings
    if (this.options.responsive) {
      for (const responsive of this.options.responsive) {
        if (windowWidth <= responsive.breakpoint) {
          currentSettings = { ...currentSettings, ...responsive.settings };
          break;
        }
      }
    }

    return currentSettings;
  }

  /**
   * Update settings based on responsive breakpoints
   */
  updateResponsiveSettings() {
    const currentSettings = this.getCurrentSettings();

    // Update arrows visibility
    const arrowsContainer = this.element.querySelector('.gallery-arrows');
    if (arrowsContainer) {
      if (currentSettings.arrows === false) {
        arrowsContainer.style.display = 'none';
      } else {
        arrowsContainer.style.display = '';
      }
    }

    // Update dots visibility
    if (this.dotsContainer) {
      if (currentSettings.dots === false) {
        this.dotsContainer.style.display = 'none';
      } else {
        this.dotsContainer.style.display = '';
      }
    }
  }

  /**
   * Destroy gallery
   */
  destroy() {
    // Stop autoplay
    this.stopAutoplay();

    // Remove event listeners
    window.removeEventListener('resize', this.updateResponsiveSettings);

    // Reset slides
    this.slides.forEach((slide, index) => {
      slide.style.display = '';
      slide.style.opacity = '';
      slide.style.transition = '';
      slide.classList.remove('gallery-slide', 'gallery-slide-active');
      slide.removeAttribute('data-slide');
      slide.removeAttribute('aria-hidden');
    });

    // Remove created elements
    const arrowsContainer = this.element.querySelector('.gallery-arrows');
    const dotsContainer = this.element.querySelector('.gallery-dots');
    const slidesContainer = this.element.querySelector('.gallery-slides');

    if (arrowsContainer) arrowsContainer.remove();
    if (dotsContainer) dotsContainer.remove();
    if (slidesContainer) {
      // Move slides back to main container
      this.slides.forEach(slide => {
        this.element.appendChild(slide);
      });
      slidesContainer.remove();
    }

    this.element.classList.remove('gallery-container');
  }
}

export default Gallery;